/** The elements that show a reader something without any text in them. */
const RENDERING_SELECTOR =
  'img,picture,video,audio,iframe,svg,canvas,object,embed,hr,br,table,input:not([type="hidden"]),select,textarea,button,[style*="background"]';

/** The elements that never show a reader anything, whatever they hold. */
const UNSHOWN_SELECTOR = 'script,style,template,noscript,[hidden]:not([hidden="until-found"])';

/**
 * The elements a rendering wraps blocks in that are no content themselves. A heading, a paragraph, a
 * list or a table is content, and is never taken apart: its parts are only content inside it.
 */
const WRAPPER_TAGS: ReadonlySet<string> = new Set(['DIV', 'SECTION', 'ARTICLE', 'MAIN']);

/** How many wrappers deep the two sides are read at most, far more than any rendering nests. */
const MOST_WRAPPERS = 64;

/** Two content areas of one page: the page the draft was made from, and the draft. */
export interface IComparedContents {
  sourceHtml: string;
  targetHtml: string;
}

function isElement(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE;
}

function isWrapper(node: Node): node is Element {
  return isElement(node) && WRAPPER_TAGS.has(node.tagName);
}

/** Whether a node shows a reader anything: text of its own, or an element with text or something rendered in it. */
function showsSomething(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || '').trim().length > 0;
  }

  if (!isElement(node) || node.matches(UNSHOWN_SELECTOR)) {
    return false;
  }

  return node.matches(RENDERING_SELECTOR) || Array.from(node.childNodes).some(showsSomething);
}

/** The children of a node as it is read: its text, and the elements that show anything. */
function readChildren(node: Node): Node[] {
  return Array.from(node.childNodes).filter((child) => child.nodeType === Node.TEXT_NODE || showsSomething(child));
}

/** What an element is known by where it is styled, when it has an id or a class; a bare element is known by none. */
function identityOf(element: Element): string | undefined {
  if (!element.id && !element.classList.length) {
    return undefined;
  }

  return `${element.tagName}#${element.id}.${Array.from(element.classList).sort().join('.')}`;
}

/** The text each element shows, read once: the counterparts of a page are asked about it again and again. */
const textByElement = new WeakMap<Element, string>();

/** The text an element shows, with its white space collapsed. */
function textOf(element: Element): string {
  let text = textByElement.get(element);
  if (text === undefined) {
    text = (element.textContent || '').replace(/\s+/g, ' ').trim();
    textByElement.set(element, text);
  }

  return text;
}

/**
 * Whether two elements are the same block of the page in two renderings: the same id and classes, or,
 * where neither has any, the same tag and the same text.
 */
function isCounterpart(one: Element, other: Element): boolean {
  const identity = identityOf(one);
  if (identity !== undefined || identityOf(other) !== undefined) {
    return identity === identityOf(other);
  }

  return one.tagName === other.tagName && textOf(one) === textOf(other);
}

/**
 * Whether two wrappers are the same container of the page: the same tag with the same attributes, so
 * that nothing about the container itself changed. Their text is what the comparison is about, so it
 * does not decide.
 */
function isSameContainer(one: Element, other: Element): boolean {
  if (!isWrapper(one) || !isWrapper(other) || one.tagName !== other.tagName || one.attributes.length !== other.attributes.length) {
    return false;
  }

  return Array.from(one.attributes).every((attribute) => other.getAttribute(attribute.name) === attribute.value);
}

/** The elements of one list that have a counterpart among the given elements. */
function counterpartsAmong(elements: Element[], candidates: Element[]): Element[] {
  return elements.filter((element) => candidates.some((candidate) => isCounterpart(candidate, element)));
}

/** The elements of a node and everything inside it. */
function elementsWithin(node: Node): Element[] {
  return isElement(node) ? [node, ...Array.from(node.querySelectorAll('*'))] : [];
}

/**
 * The blocks of one side with every wrapper around the other side's blocks replaced by what it shows,
 * when there is one. Such a wrapper is no block of the other side itself, and it holds every block of
 * the other side that this side shows anywhere: a wrapper around some of them only is a change of the
 * draft, such as blocks grouped into a box, and stays.
 */
function unwrapAround(blocks: Node[], others: Node[]): Node[] | undefined {
  const otherElements = others.filter(isElement);
  const shownHere = counterpartsAmong(otherElements, blocks.flatMap(elementsWithin));
  let unwrapped = false;
  const read = blocks.flatMap((block) => {
    if (!isWrapper(block) || otherElements.some((other) => isCounterpart(block, other))) {
      return [block];
    }

    const heldInside = counterpartsAmong(otherElements, Array.from(block.querySelectorAll('*')));
    if (!heldInside.length || heldInside.length !== shownHere.length) {
      return [block];
    }

    unwrapped = true;
    return readChildren(block);
  });

  return unwrapped ? read : undefined;
}

/** The only element of one side's blocks, when it shows nothing beside it. */
function onlyElementOf(blocks: Node[]): Element | undefined {
  const elements = blocks.filter(isElement);
  const text = blocks.some((node) => node.nodeType === Node.TEXT_NODE && showsSomething(node));

  return elements.length === 1 && !text ? elements[0] : undefined;
}

/**
 * The levels one side's only wrapper can be read at: the wrapper itself, what it shows, and what each
 * wrapper shows that is the only thing shown at the level above it.
 */
function levelsOf(only: Element): Node[][] {
  const levels: Node[][] = [[only]];
  let current = only;
  for (let depth = 0; depth < MOST_WRAPPERS; depth += 1) {
    const inside = readChildren(current);
    levels.push(inside);
    const next = onlyElementOf(inside);
    if (!next || !isWrapper(next)) {
      break;
    }

    current = next;
  }

  return levels;
}

/** How many blocks of one level have a counterpart of their own in another. */
function sharedBlocks(one: Node[], other: Node[]): number {
  const others = other.filter(isElement);
  const matched = new Set<Element>();
  let shared = 0;
  one.filter(isElement).forEach((element) => {
    const counterpart = others.find((candidate) => !matched.has(candidate) && isCounterpart(element, candidate));
    if (counterpart) {
      matched.add(counterpart);
      shared += 1;
    }
  });

  return shared;
}

/**
 * Where two sides that each show one wrapper only meet: the pair of their levels whose blocks share
 * the most counterparts, the shallower one of two that share as many, so that a block met whole is
 * never taken apart. Nothing when no level of one shares a block with a level of the other, or when
 * they meet at the wrappers themselves.
 */
function meetingLevels(sourceOnly: Element, targetOnly: Element): { source: Node[]; target: Node[] } | undefined {
  const sourceLevels = levelsOf(sourceOnly);
  const targetLevels = levelsOf(targetOnly);

  // One block both sides show whole is where they meet, at the shallowest levels that show it: read
  // inside it, a change of the block itself, its style for one, would not show.
  let whole: { source: number; target: number } | undefined;
  sourceLevels.forEach((sourceLevel, sourceDepth) => {
    targetLevels.forEach((targetLevel, targetDepth) => {
      const sourceBlock = onlyElementOf(sourceLevel);
      const targetBlock = onlyElementOf(targetLevel);
      const shallower = !whole || sourceDepth + targetDepth < whole.source + whole.target;
      if (sourceBlock && targetBlock && isCounterpart(sourceBlock, targetBlock) && shallower) {
        whole = { source: sourceDepth, target: targetDepth };
      }
    });
  });
  if (whole && (whole.source > 0 || whole.target > 0)) {
    return { source: sourceLevels[whole.source], target: targetLevels[whole.target] };
  }

  let best: { source: number; target: number; shared: number } | undefined;
  sourceLevels.forEach((sourceLevel, sourceDepth) => {
    targetLevels.forEach((targetLevel, targetDepth) => {
      const shared = sharedBlocks(sourceLevel, targetLevel);
      const shallower = !best || sourceDepth + targetDepth < best.source + best.target;
      if (shared > 0 && (!best || shared > best.shared || (shared === best.shared && shallower))) {
        best = { source: sourceDepth, target: targetDepth, shared };
      }
    });
  });

  if (!best || (best.source === 0 && best.target === 0)) {
    return undefined;
  }

  return { source: sourceLevels[best.source], target: targetLevels[best.target] };
}

/** The markup of one side's blocks, in their order, its text escaped as markup is. */
function htmlOf(blocks: Node[]): string {
  if (!blocks.length) {
    return '';
  }

  const holder = (blocks[0].ownerDocument || document).createElement('div');
  blocks.forEach((block) => holder.appendChild(block.cloneNode(true)));

  return holder.innerHTML;
}

/**
 * Two content areas of one page, read so that they compare block by block.
 *
 * Two renderings of one page differ in more than their content. The page visitors are shown and the
 * page a site renders its draft as can wrap the same blocks in elements of their own, show something
 * beside them, and carry elements that show nothing: an anchor to skip to, a form of hidden fields.
 * Compared as they are, every block sits at another depth, and each is shown as changed although one
 * heading differs.
 *
 * Both sides are therefore read through this one routine, from the page down. Where each side shows
 * one wrapper only, the two are read inside the same container, compared whole when they are the same
 * block with the block itself changed, or read at the levels of the two whose blocks share the most
 * counterparts. Otherwise a wrapper that is no block of the other side, but holds
 * every block of it this side shows, is taken away on its side and what it shows takes its place,
 * beside whatever else that side shows there, until neither side has such a wrapper left. What is left
 * out on the way is only what shows nothing and the wrappers around the blocks both sides show; text
 * between blocks is kept. A side that shows nothing when it has been read is compared as it came, with
 * the other side as it came.
 */
export function toComparableContents(contents: IComparedContents): IComparedContents {
  const parser = new DOMParser();
  let source = readChildren(parser.parseFromString(contents.sourceHtml, 'text/html').body);
  let target = readChildren(parser.parseFromString(contents.targetHtml, 'text/html').body);

  for (let depth = 0; depth < MOST_WRAPPERS; depth += 1) {
    const sourceOnly = onlyElementOf(source);
    const targetOnly = onlyElementOf(target);
    if (sourceOnly && targetOnly && isSameContainer(sourceOnly, targetOnly)) {
      source = readChildren(sourceOnly);
      target = readChildren(targetOnly);
      continue;
    }

    // The same block with something about the block itself changed, its style for one: it is compared
    // whole, so that the change shows.
    if (sourceOnly && targetOnly && isCounterpart(sourceOnly, targetOnly)) {
      break;
    }

    const met =
      sourceOnly && targetOnly && isWrapper(sourceOnly) && isWrapper(targetOnly) ? meetingLevels(sourceOnly, targetOnly) : undefined;
    if (met) {
      source = met.source;
      target = met.target;
      continue;
    }

    const unwrappedTarget = unwrapAround(target, source);
    if (unwrappedTarget) {
      target = unwrappedTarget;
      continue;
    }

    const unwrappedSource = unwrapAround(source, target);
    if (unwrappedSource) {
      source = unwrappedSource;
      continue;
    }

    break;
  }

  const read = { sourceHtml: htmlOf(source), targetHtml: htmlOf(target) };
  if (!read.sourceHtml.trim() || !read.targetHtml.trim()) {
    return { sourceHtml: contents.sourceHtml, targetHtml: contents.targetHtml };
  }

  return read;
}
